import { Injectable } from '@angular/core';
import { PERMISSIONS } from '@phdsoft/common';
import { BehaviorSubject, Observable } from 'rxjs';

export type SupportedLanguage = 'en' | 'pt-br';

@Injectable({
  providedIn: 'root'
})
export class LanguagePreferenceService {
  private readonly STORAGE_KEY = 'phdsoft-language-preference';
  private readonly DEFAULT_LANGUAGE: SupportedLanguage = 'en';

  private languageSubject: BehaviorSubject<SupportedLanguage>;
  public language$: Observable<SupportedLanguage>;

  constructor() {
    const initialLanguage = this.getInitialLanguage();
    this.languageSubject = new BehaviorSubject<SupportedLanguage>(initialLanguage);
    this.language$ = this.languageSubject.asObservable();
  }

  private getInitialLanguage(): SupportedLanguage {
    // Priority: 1. Stored preference, 2. Browser language, 3. Default
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored && this.isValidLanguage(stored)) {
      return stored as SupportedLanguage;
    }

    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('pt')) {
      return 'pt-br';
    }

    return this.DEFAULT_LANGUAGE;
  }

  private isValidLanguage(lang: string): boolean {
    return lang === 'en' || lang === 'pt-br';
  }

  getCurrentLanguage(): SupportedLanguage {
    return this.languageSubject.value;
  }

  setLanguage(language: SupportedLanguage): void {
    if (!this.isValidLanguage(language)) {
      console.warn(`Invalid language: ${language}. Using default: ${this.DEFAULT_LANGUAGE}`);
      return;
    }

    localStorage.setItem(this.STORAGE_KEY, language);
    this.languageSubject.next(language);
  }

  getSoftwareManualPermission(): string {
    return PERMISSIONS.SHOW_SOFTWARE_MANUAL;
  }

  getCurrentLanguageDisplayName(): string {
    const lang = this.getCurrentLanguage();
    return lang === 'pt-br' ? 'Português (Brasil)' : 'English';
  }

  getCurrentLanguageFlag(): string {
    const lang = this.getCurrentLanguage();
    return lang === 'pt-br' ? '🇧🇷' : '🇺🇸';
  }
}
